import * as signalR from '@microsoft/signalr'
import { refreshSessionToken } from '@proxies/auth'
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
  useRef,
  useCallback,
} from 'react'
import { getProxyUrl } from '../proxies'

export type Acabado = {
  IdAcabadoUnidad: number
  IdTexturas: number
}
export type Unidad = {
  IdFEUnidad: string
  IdUnidades: number
  Cantidad?: number
  Ancho?: number
  Alto?: number
  Fondo?: number
  Acabados?: Acabado[]
  Enlaces?: Unidad[]
}
export type CalcularPreciosData = {
  Unidades: Unidad[]
  DarTotalLineas: boolean
  DarTotalOtros: boolean
}
export type Linea = {
  Cantidad?: number
  PrecioUnitario?: number
  PrecioTotal?: number
}
export type UnidadCreada = {
  Codigo: string
  Descripcion: string
  Ruta?: string
  Miniatura?: string
  Cantidad?: number
  PrecioUnitario?: number
  PrecioTotal?: number
  IdsFEUnidades: string[]
}
export type Zocalo = UnidadCreada & {
  MetrosLineales?: number
  Tiras?: number
}
export type EncimeraYFrontal = UnidadCreada & {
  IsFrontal: boolean
  Ancho?: number
  Alto?: number
  Fondo?: number
}
export type CalculoPreciosResult = {
  msgError?: string
  precioTotal: number
  idAndPrecios?: Record<string, Linea>
  zocalos: Zocalo[]
  encimeraYFrontales: EncimeraYFrontal[]
}
type PreciosHubContextType = {
  connection: signalR.HubConnection | null
  calcularPrecios: (data: CalcularPreciosData) => Promise<void>
  resultadoPrecios: CalculoPreciosResult | null
  errorPrecios: string | null
  isConnected: boolean
  connect: () => Promise<void>
  disconnect: () => Promise<void>
  connectionStatus: 'connected' | 'connecting' | 'disconnected' | 'error'
}

export const PreciosHubContext = createContext<PreciosHubContextType | undefined>(undefined)
type PropChildren = { children: ReactNode }

const isUnauthorizedError = (error: unknown) => {
  const msg = String(
    (error as { message?: string; statusCode?: number; status?: number })?.message || error || '',
  )
  const status = Number(
    (error as { statusCode?: number; status?: number })?.statusCode ??
      (error as { status?: number })?.status ??
      0,
  )
  return status === 401 || /status code ['"]?401['"]?/i.test(msg) || /\b401\b/.test(msg)
}

export default function PreciosHubProvider({ children }: PropChildren) {
  const [connection, setConnection] = useState<signalR.HubConnection | null>(null)
  const [resultadoPrecios, setResultadoPrecios] = useState<CalculoPreciosResult | null>(null)
  const [errorPrecios, setErrorPrecios] = useState<string | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState<
    'connected' | 'connecting' | 'disconnected' | 'error'
  >('disconnected')

  const connectionRef = useRef<signalR.HubConnection | null>(null)
  const isConnectingRef = useRef(false)
  const reconnectAttemptRef = useRef(0)
  const tokenInUseRef = useRef<string | null>(null)
  const refreshInFlightRef = useRef(false)
  const authBlockedUntilRef = useRef(0)
  const maxReconnectAttempts = 5

  const getToken = useCallback((): string | null => {
    try {
      return sessionStorage.getItem('access_token')
    } catch (error) {
      console.error('Error al obtener token:', error)
      return null
    }
  }, [])

  const canConnectWithToken = useCallback((): boolean => {
    const token = getToken()
    // Permitir conexión también con token de Public (sin login tradicional).
    return !!token && token !== 'null' && token !== 'undefined'
  }, [getToken])

  const renewTokenAfterUnauthorized = useCallback(async (): Promise<boolean> => {
    if (refreshInFlightRef.current) return false
    if (Date.now() < authBlockedUntilRef.current) return false

    refreshInFlightRef.current = true
    try {
      const previous = getToken()
      const next = await refreshSessionToken()
      if (!next) {
        // Evitar martillar newSession / negotiate con el mismo token muerto.
        authBlockedUntilRef.current = Date.now() + 15_000
        return false
      }
      if (next === previous) {
        // newSession no cambió el token: seguir bloqueando un rato.
        authBlockedUntilRef.current = Date.now() + 15_000
        return false
      }
      authBlockedUntilRef.current = 0
      reconnectAttemptRef.current = 0
      return true
    } finally {
      refreshInFlightRef.current = false
    }
  }, [getToken])

  const connect = useCallback(async (): Promise<void> => {
    if (Date.now() < authBlockedUntilRef.current) {
      return
    }

    const tokenUser = getToken()

    if (!canConnectWithToken()) {
      return
    }

    if (
      isConnectingRef.current ||
      connectionRef.current?.state === signalR.HubConnectionState.Connected
    ) {
      return
    }

    try {
      isConnectingRef.current = true
      setConnectionStatus('connecting')

      if (connectionRef.current) {
        await connectionRef.current.stop().catch(console.error)
      }

      const newConnection = new signalR.HubConnectionBuilder()
        .withUrl(getProxyUrl('preciosHub'), {
          // Leer el token en cada request (negotiate / long-poll), no el capturado al crear.
          headers: {
            get Authorization() {
              const live = sessionStorage.getItem('access_token')
              return live ? `Ninpo ${live}` : `Ninpo ${tokenUser}`
            },
          },
          transport: signalR.HttpTransportType.LongPolling,
        })
        .configureLogging(signalR.LogLevel.Warning)
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext) => {
            if (retryContext.previousRetryCount >= maxReconnectAttempts) {
              return null // Stop retrying
            }
            return Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000)
          },
        })
        .build()

      newConnection.onreconnected(() => {
        setIsConnected(true)
        setConnectionStatus('connected')
        setErrorPrecios(null)
        reconnectAttemptRef.current = 0
      })

      newConnection.onreconnecting(() => {
        setIsConnected(false)
        setConnectionStatus('connecting')
        reconnectAttemptRef.current++
      })

      newConnection.onclose((error) => {
        setIsConnected(false)
        setConnectionStatus('disconnected')

        if (error && canConnectWithToken()) {
          setTimeout(async () => {
            if (isUnauthorizedError(error)) {
              const renewed = await renewTokenAfterUnauthorized()
              if (renewed) connect()
              return
            }
            if (reconnectAttemptRef.current < maxReconnectAttempts) {
              connect()
            }
          }, 2000)
        }
      })

      newConnection.on('NotificacionCalculoPrecios', (result: CalculoPreciosResult) => {
        setResultadoPrecios(result)
        setErrorPrecios(result.msgError ?? null)
      })

      await newConnection.start()

      connectionRef.current = newConnection
      tokenInUseRef.current = getToken()
      setConnection(newConnection)
      setIsConnected(true)
      setConnectionStatus('connected')
      setErrorPrecios(null)
      reconnectAttemptRef.current = 0
      authBlockedUntilRef.current = 0
    } catch (error) {
      console.error('❌ Error conectando a SignalR:', error)
      setErrorPrecios('Error de conexión con el servidor')
      setIsConnected(false)
      setConnectionStatus('error')

      if (isUnauthorizedError(error)) {
        const renewed = await renewTokenAfterUnauthorized()
        if (renewed) {
          setTimeout(() => {
            connect()
          }, 400)
        }
        return
      }

      reconnectAttemptRef.current++
      if (reconnectAttemptRef.current < maxReconnectAttempts && canConnectWithToken()) {
        setTimeout(() => {
          connect()
        }, 3000)
      }
    } finally {
      isConnectingRef.current = false
    }
  }, [getToken, canConnectWithToken, renewTokenAfterUnauthorized])

  const disconnect = useCallback(async (): Promise<void> => {
    try {
      reconnectAttemptRef.current = maxReconnectAttempts // Evitar reconexiones

      if (connectionRef.current) {
        await connectionRef.current.stop()
        connectionRef.current = null
        tokenInUseRef.current = null
        setConnection(null)
      }

      setIsConnected(false)
      setConnectionStatus('disconnected')
    } catch (error) {
      console.error('Error desconectando SignalR:', error)
    }
  }, [])

  useEffect(() => {
    const checkAuthAndConnect = () => {
      if (canConnectWithToken()) {
        connect()
      } else {
        disconnect()
      }
    }

    checkAuthAndConnect()

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'access_token') {
        authBlockedUntilRef.current = 0
        checkAuthAndConnect()
      }
    }

    const handleAuthChange = () => {
      authBlockedUntilRef.current = 0
      checkAuthAndConnect()
    }

    const handleVisibility = async () => {
      if (document.visibilityState !== 'visible') return

      const hasConnection = connectionRef.current?.state === signalR.HubConnectionState.Connected
      if (hasConnection || isConnectingRef.current) return

      // Tras inactividad SignalR suele estar caído y el token en sessionStorage caducado.
      try {
        const previous = getToken()
        const next = await refreshSessionToken()
        authBlockedUntilRef.current = 0
        if (next && next !== previous) {
          reconnectAttemptRef.current = 0
        }
      } catch {
        // Si falla el refresh, intentamos conectar con lo que haya.
      }

      if (canConnectWithToken() && !isConnectingRef.current) {
        connect()
      }
    }

    const interval = setInterval(() => {
      if (Date.now() < authBlockedUntilRef.current) return

      const currentAuth = canConnectWithToken()
      const currentToken = getToken()
      const hasConnection = connectionRef.current?.state === signalR.HubConnectionState.Connected

      if (!currentAuth) {
        if (hasConnection) {
          disconnect()
        }
        return
      }

      // Si cambió el token (ej: Public -> User tras login), reconectar con el nuevo.
      if (
        hasConnection &&
        currentToken &&
        tokenInUseRef.current &&
        currentToken !== tokenInUseRef.current &&
        !isConnectingRef.current
      ) {
        ;(async () => {
          await disconnect()
          await connect()
        })()
        return
      }

      if (currentAuth && !hasConnection && !isConnectingRef.current) {
        checkAuthAndConnect()
      } else if (!currentAuth && hasConnection) {
        disconnect()
      }
    }, 2000)

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('authChange', handleAuthChange)
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      clearInterval(interval)
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('authChange', handleAuthChange)
      document.removeEventListener('visibilitychange', handleVisibility)
      disconnect()
    }
  }, [connect, disconnect, canConnectWithToken, getToken, renewTokenAfterUnauthorized])

  const calcularPrecios = useCallback(
    async (data: CalcularPreciosData): Promise<void> => {
      if (!data.Unidades?.length) {
        setResultadoPrecios({
          precioTotal: 0,
          zocalos: [],
          encimeraYFrontales: [],
          idAndPrecios: {},
        })
      }

      if (!connectionRef.current) {
        throw new Error('No hay conexión disponible. Intentando reconectar...')
      }

      if (connectionRef.current.state !== signalR.HubConnectionState.Connected) {
        throw new Error(`Conexión no disponible. Estado: ${connectionRef.current.state}`)
      }

      try {
        setErrorPrecios(null)
        await connectionRef.current.invoke('CalcularPrecios', data)
      } catch (error: any) {
        console.error('Error al calcular precios:', error)

        const errorMessage = error.message || 'Error al calcular precios'
        setErrorPrecios(errorMessage)

        if (isUnauthorizedError(error)) {
          const renewed = await renewTokenAfterUnauthorized()
          if (renewed) await connect()
        } else if (errorMessage.includes('conexión') || errorMessage.includes('connection')) {
          await connect()
        }

        throw error
      }
    },
    [connect, renewTokenAfterUnauthorized],
  )

  const contextValue: PreciosHubContextType = {
    connection,
    calcularPrecios,
    resultadoPrecios,
    errorPrecios,
    isConnected,
    connect,
    disconnect,
    connectionStatus,
  }

  return <PreciosHubContext.Provider value={contextValue}>{children}</PreciosHubContext.Provider>
}

export const usePreciosHubContext = () => {
  const context = useContext(PreciosHubContext)
  if (context === undefined) {
    throw new Error('usePreciosHubContext debe usarse dentro de un PreciosHubProvider')
  }
  return context
}
